/**
 * GlobalAudioPlayer - Persistent Audio Player (Alexa-style)
 * 
 * Features:
 * - Fixiert über Bottom-Navigation (bottom-[64px])
 * - Shows current radio station when playing
 * - Previous / Play-Pause / Stop controls
 * - Click on card area opens MusicView
 * - LIVE badge for radio streams
 */
import React from 'react';
import { Play, Pause, X, SkipBack, SkipForward } from 'lucide-react';
import { useAudioPlayer } from '../../contexts/AudioPlayerContext';

export default function GlobalAudioPlayer({ onOpenMusic }) {
  const { isPlaying, isLoading, currentStation, pause, resume, stop, previous, next } = useAudioPlayer();

  // Don't show if nothing is selected
  if (!currentStation) {
    return null;
  }

  const handleCardClick = (e) => {
    // Only open music if not clicking on buttons
    if (e.target.closest('button')) return;
    if (onOpenMusic) onOpenMusic();
  };

  return (
    <div className="fixed left-0 right-0 bottom-[64px] mx-auto max-w-screen-md px-4 z-[60]">
      <div 
        onClick={handleCardClick}
        className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl shadow-2xl p-3 backdrop-blur-sm cursor-pointer hover:shadow-blue-500/50 transition-all"
      >
        <div className="flex items-center justify-between gap-3">
          {/* Left: Station Info */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="text-2xl">{currentStation.logo || currentStation.icon || '📻'}</div>
            <div className="flex-1 min-w-0">
              <h4 className="text-white font-bold text-base truncate">
                {currentStation.name}
              </h4>
              <div className="flex items-center gap-2">
                <span className="text-blue-100 text-xs truncate">
                  {isPlaying ? 'Läuft gerade' : 'Pausiert'}
                </span>
                {/* LIVE Badge */}
                {isPlaying && (
                  <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded animate-pulse">
                    LIVE
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Right: Controls */}
          <div className="flex items-center gap-1">
            {/* Previous */}
            <button
              onClick={(e) => { e.stopPropagation(); previous(); }}
              className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-all"
              title="Vorheriger Sender"
            >
              <SkipBack className="w-4 h-4 text-white" />
            </button>

            {/* Play/Pause */}
            <button
              onClick={(e) => { e.stopPropagation(); isPlaying ? pause() : resume(); }}
              disabled={isLoading}
              className="w-11 h-11 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-all disabled:opacity-50"
              title={isPlaying ? 'Pausieren' : 'Abspielen'}
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              ) : isPlaying ? (
                <Pause className="w-5 h-5 text-blue-600" />
              ) : (
                <Play className="w-5 h-5 text-blue-600 ml-0.5" />
              )}
            </button>

            {/* Next */}
            <button
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-all"
              title="Nächster Sender"
            >
              <SkipForward className="w-4 h-4 text-white" />
            </button>

            {/* Stop */}
            <button
              onClick={(e) => { e.stopPropagation(); stop(); }}
              className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-all"
              title="Stoppen"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
